// src/pages/SchoolOverview.tsx
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaRegCalendarCheck,
  FaSchool,
} from "react-icons/fa";

import { useCan } from "../authz/useCan";
import {
  LdsOverviewCard,
  LdsOverviewEmpty,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
  LdsOverviewSection,
} from "../components/lds/LdsOverview";

export default function SchoolOverview() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const canListTeachers = useCan("school.teachers", "list");
  const canReadTeachers = useCan("school.teachers", "read");
  const canCrudTeachers = useCan("school.teachers", "crud");
  const canManageTeachers = useCan("school.teachers", "manage");
  const canLinkSelfTeacher = useCan("school.teachers", "linkSelf");

  const canViewClassrooms = useCan("school.classrooms", "view");
  const canListClassrooms = useCan("school.classrooms", "list");
  const canReadClassrooms = useCan("school.classrooms", "read");
  const canCrudClassrooms = useCan("school.classrooms", "crud");
  const canManageClassrooms = useCan("school.classrooms", "manage");

  const canListYears = useCan("school.years", "list");
  const canReadYears = useCan("school.years", "read");
  const canViewYears = useCan("school.years", "view");
  const canCrudYears = useCan("school.years", "crud");
  const canManageYears = useCan("school.years", "manage");

  const canReadSchoolCalendar = useCan("school.calendar", "read");
  const canManageSchoolCalendar = useCan("school.calendar", "manage");

  const canSeeTeachers =
    canListTeachers ||
    canReadTeachers ||
    canCrudTeachers ||
    canManageTeachers ||
    canLinkSelfTeacher;

  const canSeeClassrooms =
    canViewClassrooms ||
    canListClassrooms ||
    canReadClassrooms ||
    canCrudClassrooms ||
    canManageClassrooms;

  const canSeeYears =
    canListYears ||
    canReadYears ||
    canViewYears ||
    canCrudYears ||
    canManageYears;

  const canSeeSchoolCalendar =
    canReadSchoolCalendar || canManageSchoolCalendar;

  const visibleSections = [
    canSeeTeachers,
    canSeeClassrooms,
    canSeeYears,
    canSeeSchoolCalendar,
  ].filter(Boolean).length;

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaSchool}
        eyebrow={t("school.overview.eyebrow", {
          defaultValue: "School",
        })}
        title={t("school.overview.heading", {
          defaultValue: "School management",
        })}
        description={t(
          "school.overview.description",
          {
            defaultValue:
              "Manage the core school setup: teachers, classrooms, school years and school planning.",
          }
        )}
      />

      {visibleSections > 0 ? (
        <LdsOverviewSection
          title={t(
            "school.overview.sections.title",
            { defaultValue: "Management sections" }
          )}
          description={t(
            "school.overview.sections.description",
            {
              defaultValue:
                "Choose the school section you want to manage.",
            }
          )}
        >
          <LdsOverviewGrid>
            {canSeeTeachers ? (
              <LdsOverviewCard
                icon={FaChalkboardTeacher}
                eyebrow={t(
                  "school.overview.cards.teachers.category",
                  { defaultValue: "Staff" }
                )}
                title={t(
                  "school.overview.cards.teachers.title",
                  { defaultValue: "Teachers" }
                )}
                description={t(
                  "school.overview.cards.teachers.description",
                  {
                    defaultValue:
                      "Manage teachers and teacher assignments.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate("/school/teachers")
                }
                animationIndex={0}
              />
            ) : null}

            {canSeeClassrooms ? (
              <LdsOverviewCard
                icon={FaSchool}
                eyebrow={t(
                  "school.overview.cards.classrooms.category",
                  { defaultValue: "Structure" }
                )}
                title={t(
                  "school.overview.cards.classrooms.title",
                  { defaultValue: "Classrooms" }
                )}
                description={t(
                  "school.overview.cards.classrooms.description",
                  {
                    defaultValue:
                      "View and manage classrooms.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate("/school/classrooms")
                }
                animationIndex={1}
              />
            ) : null}

            {canSeeYears ? (
              <LdsOverviewCard
                icon={FaCalendarAlt}
                eyebrow={t(
                  "school.overview.cards.years.category",
                  { defaultValue: "Planning" }
                )}
                title={t(
                  "school.overview.cards.years.title",
                  { defaultValue: "School years" }
                )}
                description={t(
                  "school.overview.cards.years.description",
                  {
                    defaultValue:
                      "Manage school years and their planning defaults.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate("/school/years")
                }
                animationIndex={2}
              />
            ) : null}

            {canSeeSchoolCalendar ? (
              <LdsOverviewCard
                icon={FaRegCalendarCheck}
                eyebrow={t(
                  "school.overview.cards.schoolCalendar.category",
                  { defaultValue: "Calendar" }
                )}
                title={t(
                  "school.overview.cards.schoolCalendar.title",
                  {
                    defaultValue:
                      "School planning",
                  }
                )}
                description={t(
                  "school.overview.cards.schoolCalendar.description",
                  {
                    defaultValue:
                      "Manage school holidays, planning items and school-wide events.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate(
                    "/school/school-calendar"
                  )
                }
                animationIndex={3}
              />
            ) : null}
          </LdsOverviewGrid>
        </LdsOverviewSection>
      ) : (
        <LdsOverviewEmpty>
          {t("school.overview.empty", {
            defaultValue:
              "No school sections are available with your current permissions.",
          })}
        </LdsOverviewEmpty>
      )}
    </LdsOverviewPage>
  );
}
